(() => {
  if (window.productPollCollectionLoaded) return;
  window.productPollCollectionLoaded = true;

  const CHOICE_ORDER = ['yes_want', 'interested'];
  const AUTO_SAVE_DELAY_MS = 900;
  const DRAFT_PREFIX = 'product-poll-draft:';

  const STOREFRONT_CHOICE_LABELS = {
    yes_want: 'Make this next',
    interested: "I'd buy this",
  };

  const qs = (root, selector) => root.querySelector(selector);
  const qsa = (root, selector) => [...root.querySelectorAll(selector)];

  const debounce = (fn, delay) => {
    let timer = null;
    return (...args) => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => fn(...args), delay);
    };
  };

  const stripHtml = (value) => {
    const text = String(value || '')
      .replace(/<br\s*\/?>/gi, ' ')
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    return text.length > 120 ? `${text.slice(0, 117)}...` : text;
  };

  const draftStorageKey = (pollId) => `${DRAFT_PREFIX}${pollId}`;

  const loadDraft = (pollId) => {
    try {
      const raw = localStorage.getItem(draftStorageKey(pollId));
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };

  const saveDraft = (pollId, draft) => {
    try {
      localStorage.setItem(draftStorageKey(pollId), JSON.stringify(draft));
    } catch {
      /* storage full or blocked */
    }
  };

  const choicesFingerprint = (choices) =>
    JSON.stringify(
      [...choices].sort((a, b) => a.product_id.localeCompare(b.product_id)),
    );

  const openModal = (modal) => {
    if (!modal) return;
    modal.hidden = false;
    document.documentElement.classList.add('product-poll-collection-open');
    const firstInput = modal.querySelector('input:not([type="hidden"])');
    if (firstInput) firstInput.focus();
  };

  const closeModal = (modal) => {
    if (!modal) return;
    modal.hidden = true;
    if (!document.querySelector('[data-product-poll-modal]:not([hidden]), [data-product-poll-leave-modal]:not([hidden])')) {
      document.documentElement.classList.remove('product-poll-collection-open');
    }
  };

  const setMessage = (root, type, text) => {
    const message = qs(root, '[data-product-poll-message]');
    if (!message) return;
    message.hidden = !text;
    message.classList.remove(
      'product-poll-collection__message--success',
      'product-poll-collection__message--error',
    );
    if (type === 'success') message.classList.add('product-poll-collection__message--success');
    if (type === 'error') message.classList.add('product-poll-collection__message--error');
    message.textContent = text || '';
  };

  const buildPollUrl = (apiBase, pollId, customer) => {
    const url = new URL(`${apiBase.replace(/\/$/, '')}/api/polls/${encodeURIComponent(pollId)}/public`);
    if (customer?.id && customer?.email) {
      url.searchParams.set('customer_id', customer.id);
      url.searchParams.set('email', customer.email);
    }
    return url.toString();
  };

  const getVisibleCards = (root) =>
    qsa(root, '[data-product-poll-card]').filter((card) => !card.hidden);

  const getSelectedChoice = (card) => {
    const selected = card.querySelector('.product-poll-choice.is-selected');
    return selected?.dataset.choiceValue || '';
  };

  const collectChoices = (root) => {
    const choices = [];
    for (const card of getVisibleCards(root)) {
      const productId = card.getAttribute('data-poll-product-id');
      const choice = getSelectedChoice(card);
      if (productId && choice) choices.push({ product_id: productId, choice });
    }
    return choices;
  };


  const resolveChoiceLabels = () => ({ ...STOREFRONT_CHOICE_LABELS });

  const setCardChoiceSelection = (card, value, selected) => {
    qsa(card, '.product-poll-choice').forEach((btn) => {
      const match = btn.dataset.choiceValue === value;
      btn.classList.toggle('is-selected', match && selected);
    });
  };

  const bindCardChoiceButtons = (card, root, onChoiceChange) => {
    if (card.dataset.pollChoicesBound === 'true') return;
    card.dataset.pollChoicesBound = 'true';

    card.addEventListener('click', (event) => {
      const button = event.target.closest('.product-poll-choice');
      if (!button || !card.contains(button)) return;

      const value = button.dataset.choiceValue || '';
      const wasSelected = button.classList.contains('is-selected');
      qsa(card, '.product-poll-choice').forEach((btn) => btn.classList.remove('is-selected'));
      if (!wasSelected && value) setCardChoiceSelection(card, value, true);
      onChoiceChange?.();
    });
  };

  const renderChoices = (card, existingChoice, root, onChoiceChange) => {
    const wraps = qsa(card, '[data-product-poll-choices]');
    if (!wraps.length) return;

    const labels = resolveChoiceLabels();

    wraps.forEach((wrap) => {
      wrap.innerHTML = '';
      CHOICE_ORDER.forEach((value) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'button product-poll-choice';
        button.dataset.choiceValue = value;
        button.textContent = labels[value] || value;
        if (existingChoice === value) button.classList.add('is-selected');
        wrap.appendChild(button);
      });
    });

    bindCardChoiceButtons(card, root, onChoiceChange);
  };

  const setCardCopy = (card, pollProduct, showDescription) => {
    const title = qs(card, '[data-product-poll-title]');
    const description = qs(card, '[data-product-poll-description]');
    if (title) title.textContent = pollProduct.title || '';
    if (description) {
      const text = stripHtml(pollProduct.description);
      description.textContent = text;
      description.hidden = !showDescription || !text;
    }
  };

  const setCardMediaFromPoll = (card, pollProduct) => {
    const mediaLink = qs(card, '.product-card--featured-image-link');
    if (!mediaLink || !pollProduct.image_url) return;

    const existing = qs(mediaLink, '.product-primary-image, img');
    if (existing) {
      existing.src = pollProduct.image_url;
      if (existing.srcset !== undefined) existing.srcset = pollProduct.image_url;
      return;
    }

    mediaLink.innerHTML = `<img class="product-primary-image" src="${pollProduct.image_url}" alt="" loading="lazy" width="770" height="770" />`;
  };

  const createPollColumn = () => {
    const li = document.createElement('li');
    li.className = 'column product-poll-collection__column';
    li.setAttribute('data-product-poll-card', '');
    li.innerHTML = `
      <product-card class="product-card text-center product-poll-card">
        <div class="product-poll-card__poll product-poll-card__poll--top">
          <div class="product-poll-card__choices product-poll-card__choices--row" data-product-poll-choices></div>
        </div>
        <figure class="product-card--featured-image">
          <div class="product-card--featured-image-link aspect-ratio aspect-ratio--adapt" style="--padding-bottom: 100%;">
            <div class="thb-placeholder"></div>
          </div>
        </figure>
        <div class="product-card-info product-poll-card__info">
          <span class="product-card-title product-poll-card__title" data-product-poll-title></span>
          <p class="product-poll-card__description" data-product-poll-description hidden></p>
          <div class="product-poll-card__poll product-poll-card__poll--bottom">
            <div class="product-poll-card__choices" data-product-poll-choices></div>
          </div>
        </div>
      </product-card>
    `;
    return li;
  };

  const initDesignModePreview = (root) => {
    const loading = qs(root, '[data-product-poll-loading]');
    if (loading) loading.hidden = true;

    const noop = () => updateSubmitState(root);

    qsa(root, '[data-product-poll-card]').forEach((card) => {
      card.hidden = false;
      const shopifyId = card.getAttribute('data-shopify-product-id') || 'preview';
      card.setAttribute('data-poll-product-id', `preview-${shopifyId}`);
      renderChoices(card, '', root, noop);
    });

    updateSubmitState(root);
  };

  const updateSubmitState = (root, state = {}) => {
    const submit = qs(root, '[data-product-poll-submit]');
    const status = qs(root, '[data-product-poll-status]');
    const cards = getVisibleCards(root);
    const answered = cards.filter((card) => getSelectedChoice(card)).length;
    const unsaved = state.hasUnsaved?.() ?? false;

    if (status) {
      if (state.autoSavePending) {
        status.textContent = 'Saving your choices…';
      } else if (state.autoSavedFlash) {
        status.textContent = 'Choices saved automatically.';
      } else if (!cards.length) {
        status.textContent = 'No poll products available yet.';
      } else if (!answered) {
        status.textContent = 'Select at least one product you care about, then submit.';
      } else if (unsaved) {
        status.textContent = `${answered} product${answered === 1 ? '' : 's'} selected — saving automatically.`;
      } else {
        status.textContent = `${answered} product${answered === 1 ? '' : 's'} saved.`;
      }
    }

    if (submit) {
      submit.disabled = !answered || state.manualSubmitting;
      submit.classList.toggle('is-ready', answered > 0 && !state.manualSubmitting);
    }
  };

  const initSection = async (root) => {
    const pollId = root.getAttribute('data-poll-id');
    const apiBase = root.getAttribute('data-api-base');
    const showDescription = root.getAttribute('data-show-description') === 'true';
    if (!pollId || !apiBase) return;

    if (root.getAttribute('data-design-mode') === 'true' || window.Shopify?.designMode) {
      initDesignModePreview(root);
      return;
    }

    const customer = {
      id: root.getAttribute('data-customer-id') || '',
      email: root.getAttribute('data-customer-email') || '',
      firstName: root.getAttribute('data-customer-first-name') || '',
    };
    const isLoggedIn = Boolean(customer.id && customer.email);

    const intro = qs(root, '[data-product-poll-intro]');
    const loading = qs(root, '[data-product-poll-loading]');
    const modal = qs(root, '[data-product-poll-modal]');
    const leaveModal = qs(root, '[data-product-poll-leave-modal]');
    const modalForm = qs(root, '[data-product-poll-modal-form]');
    const submit = qs(root, '[data-product-poll-submit]');
    const grid = qs(root, '[data-product-poll-grid]');

    const draft = loadDraft(pollId) || {};
    let guestIdentity = !isLoggedIn && draft.email
      ? { email: draft.email, firstName: draft.firstName || '' }
      : null;

    const state = {
      lastSavedFingerprint: '',
      allowLeave: false,
      pendingNavigationUrl: '',
      pendingBackNavigation: false,
      leaveHistoryPushed: false,
      pendingSubmit: false,
      manualSubmitting: false,
      autoSavePending: false,
      autoSavedFlash: false,
      autoSaveTimer: null,
      hasUnsaved() {
        const choices = collectChoices(root);
        if (!choices.length) return false;
        return choicesFingerprint(choices) !== state.lastSavedFingerprint;
      },
    };

    let refreshUi = () => updateSubmitState(root, state);

    const persistDraft = () => {
      const choices = collectChoices(root);
      saveDraft(pollId, {
        choices,
        email: guestIdentity?.email || draft.email || '',
        firstName: guestIdentity?.firstName || draft.firstName || '',
        updatedAt: Date.now(),
      });
    };

    const markSaved = (choices) => {
      state.lastSavedFingerprint = choicesFingerprint(choices);
      persistDraft();
      refreshUi();
    };

    const onChoiceChanged = () => {
      persistDraft();
      refreshUi();
      scheduleAutoSave();
    };

    const submitUrl = `${apiBase.replace(/\/$/, '')}/api/polls/${encodeURIComponent(pollId)}/submit`;

    const postChoices = async (identity, choices, { keepalive = false } = {}) => {
      const response = await fetch(submitUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        keepalive,
        body: JSON.stringify({
          customer_id: identity.customerId || undefined,
          email: identity.email,
          first_name: identity.firstName || undefined,
          choices,
        }),
      });
      const data = await response.json();
      if (!response.ok || !data.ok) throw new Error(data.error || 'Submit failed.');
      return data;
    };

    const submitChoices = async (identity, { silent = false, keepalive = false } = {}) => {
      const choices = collectChoices(root);
      if (!choices.length) {
        if (!silent) setMessage(root, 'error', 'Select at least one product before submitting.');
        return false;
      }

      if (!silent) {
        state.manualSubmitting = true;
        submit.disabled = true;
        setMessage(root, null, '');
        refreshUi();
      } else {
        state.autoSavePending = true;
        refreshUi();
      }

      try {
        const data = await postChoices(identity, choices, { keepalive });
        markSaved(choices);
        closeModal(modal);
        closeModal(leaveModal);

        if (!isLoggedIn) {
          guestIdentity = {
            email: identity.email,
            firstName: identity.firstName || '',
          };
          saveDraft(pollId, {
            choices,
            email: guestIdentity.email,
            firstName: guestIdentity.firstName,
            updatedAt: Date.now(),
          });
        }

        if (silent) {
          state.autoSavedFlash = true;
          refreshUi();
          setTimeout(() => {
            state.autoSavedFlash = false;
            refreshUi();
          }, 2500);
        } else {
          const savedCount = (data.saved || []).length;
          let successText =
            data.message || `Thanks — saved ${savedCount} choice${savedCount === 1 ? '' : 's'}.`;
          if (data.sync_warnings?.length) {
            successText += ` Note: ${data.sync_warnings[0]}`;
          }
          setMessage(root, 'success', successText);
        }

        if (state.pendingNavigationUrl) {
          const target = state.pendingNavigationUrl;
          state.pendingNavigationUrl = '';
          state.pendingBackNavigation = false;
          state.allowLeave = true;
          window.location.href = target;
        } else {
          state.pendingBackNavigation = false;
        }

        return true;
      } catch (error) {
        if (!silent) {
          setMessage(root, 'error', error.message || 'Submit failed. Please try again.');
        }
        return false;
      } finally {
        state.manualSubmitting = false;
        state.autoSavePending = false;
        refreshUi();
      }
    };

    const identityForAutoSave = () => {
      if (isLoggedIn) {
        return {
          customerId: customer.id,
          email: customer.email,
          firstName: customer.firstName,
        };
      }
      if (guestIdentity?.email) {
        return {
          email: guestIdentity.email,
          firstName: guestIdentity.firstName || '',
        };
      }
      return null;
    };

    const runAutoSave = async () => {
      if (!state.hasUnsaved()) return;
      const identity = identityForAutoSave();
      if (!identity) return;
      await submitChoices(identity, { silent: true });
    };

    const scheduleAutoSave = debounce(() => {
      void runAutoSave();
    }, AUTO_SAVE_DELAY_MS);

    const beginSubmitFlow = () => {
      if (!collectChoices(root).length) return;
      if (isLoggedIn) {
        void submitChoices({
          customerId: customer.id,
          email: customer.email,
          firstName: customer.firstName,
        });
        return;
      }
      if (guestIdentity?.email) {
        void submitChoices({
          email: guestIdentity.email,
          firstName: guestIdentity.firstName || '',
        });
        return;
      }
      if (!modal) {
        setMessage(root, 'error', 'Could not open the sign-in form. Please refresh the page and try again.');
        return;
      }
      state.pendingSubmit = true;
      openModal(modal);
    };

    const openLeaveModal = (navigationUrl = '', { back = false } = {}) => {
      state.pendingNavigationUrl = navigationUrl;
      state.pendingBackNavigation = back;
      openModal(leaveModal);
    };

    const bindLeaveProtection = () => {
      /* No beforeunload — browsers only allow a generic "Leave site?" message we cannot customize. */

      if (!state.leaveHistoryPushed) {
        history.pushState({ productPollLeaveGuard: pollId }, '', window.location.href);
        state.leaveHistoryPushed = true;
      }

      window.addEventListener('popstate', () => {
        if (state.allowLeave || !state.hasUnsaved()) return;
        history.pushState({ productPollLeaveGuard: pollId }, '', window.location.href);
        openLeaveModal('', { back: true });
      });

      document.addEventListener(
        'click',
        (event) => {
          if (state.allowLeave || !state.hasUnsaved()) return;
          const link = event.target.closest('a[href]');
          if (!link || link.target === '_blank' || link.hasAttribute('download')) return;
          if (link.closest('[data-product-poll-modal], [data-product-poll-leave-modal]')) return;
          const href = link.getAttribute('href') || '';
          if (!href || href.startsWith('#') || href.startsWith('javascript:')) return;

          let destination = link.href;
          try {
            const next = new URL(link.href, window.location.href);
            const current = new URL(window.location.href);
            if (next.pathname === current.pathname && next.search === current.search && next.hash === current.hash) {
              return;
            }
            destination = next.href;
          } catch {
            return;
          }

          event.preventDefault();
          event.stopPropagation();
          openLeaveModal(destination);
        },
        true,
      );

      window.addEventListener('pagehide', () => {
        if (state.allowLeave || !state.hasUnsaved()) return;
        const identity = identityForAutoSave();
        if (!identity) return;
        const choices = collectChoices(root);
        if (!choices.length) return;
        void postChoices(identity, choices, { keepalive: true }).catch(() => {});
      });
    };

    try {
      const response = await fetch(buildPollUrl(apiBase, pollId, isLoggedIn ? customer : null));
      const data = await response.json();
      if (!response.ok || !data.ok) throw new Error(data.error || 'Could not load poll.');

      if (intro && data.poll?.intro_html) intro.innerHTML = data.poll.intro_html;
      if (loading) loading.hidden = true;

      const shopifyIdToCard = new Map();
      qsa(root, '[data-product-poll-card]').forEach((card) => {
        const shopifyId = card.getAttribute('data-shopify-product-id');
        if (shopifyId) shopifyIdToCard.set(String(shopifyId), card);
      });

      const usedCards = new Set();
      const pollProducts = data.products || [];
      const mergedExisting = { ...(data.existing || {}) };

      if (Array.isArray(draft.choices)) {
        draft.choices.forEach(({ product_id, choice }) => {
          if (product_id && choice && !mergedExisting[product_id]) {
            mergedExisting[product_id] = choice;
          }
        });
      }

      pollProducts.forEach((pollProduct) => {
        let card = null;
        let fromShopifyListing = false;

        if (pollProduct.shopify_product_id) {
          card = shopifyIdToCard.get(String(pollProduct.shopify_product_id)) || null;
          fromShopifyListing = Boolean(card);
        }

        if (!card) {
          card = createPollColumn();
          grid.appendChild(card);
        }

        card.hidden = false;
        card.setAttribute('data-poll-product-id', pollProduct.id);
        if (pollProduct.shopify_product_id) {
          card.setAttribute('data-shopify-product-id', String(pollProduct.shopify_product_id));
        }

        if (fromShopifyListing) {
          const description = qs(card, '[data-product-poll-description]');
          if (description) {
            const hasText = Boolean(description.textContent.trim());
            description.hidden = !showDescription || !hasText;
          }
        } else {
          setCardCopy(card, pollProduct, showDescription);
          setCardMediaFromPoll(card, pollProduct);
        }

        renderChoices(card, mergedExisting[pollProduct.id] || '', root, onChoiceChanged);
        usedCards.add(card);
      });

      qsa(root, '[data-product-poll-card]').forEach((card) => {
        if (!usedCards.has(card)) card.hidden = true;
      });

      if (Object.keys(data.existing || {}).length) {
        markSaved(
          Object.entries(data.existing).map(([product_id, choice]) => ({
            product_id,
            choice,
          })),
        );
      } else {
        persistDraft();
      }

      refreshUi();
      bindLeaveProtection();
    } catch (error) {
      if (loading) loading.textContent = error.message || 'Could not load poll.';
      if (submit) submit.disabled = true;
      return;
    }

    submit?.addEventListener('click', () => {
      if (submit.disabled) return;
      beginSubmitFlow();
    });

    qs(root, '[data-product-poll-leave-save]')?.addEventListener('click', () => {
      closeModal(leaveModal);
      beginSubmitFlow();
    });

    qs(root, '[data-product-poll-leave-discard]')?.addEventListener('click', () => {
      state.allowLeave = true;
      closeModal(leaveModal);
      const target = state.pendingNavigationUrl;
      const goBack = state.pendingBackNavigation;
      state.pendingNavigationUrl = '';
      state.pendingBackNavigation = false;
      if (goBack) {
        history.back();
      } else if (target) {
        window.location.href = target;
      }
    });

    qsa(root, '[data-product-poll-leave-dismiss]').forEach((node) => {
      node.addEventListener('click', () => {
        closeModal(leaveModal);
        state.pendingNavigationUrl = '';
        state.pendingBackNavigation = false;
      });
    });

    modalForm?.addEventListener('submit', (event) => {
      event.preventDefault();
      if (!state.pendingSubmit) return;

      const firstName = qs(modalForm, '[name="first_name"]')?.value.trim() || '';
      const email = qs(modalForm, '[name="email"]')?.value.trim() || '';
      if (!email) {
        setMessage(root, 'error', 'Please enter your email address.');
        return;
      }

      guestIdentity = { email, firstName };
      state.pendingSubmit = false;
      void submitChoices({ email, firstName });
    });

    document.addEventListener('click', (event) => {
      const closeButton = event.target.closest('[data-product-poll-close]');
      if (closeButton) {
        closeModal(closeButton.closest('[data-product-poll-modal]'));
        state.pendingSubmit = false;
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      qsa(document, '[data-product-poll-modal]:not([hidden]), [data-product-poll-leave-modal]:not([hidden])').forEach((node) => {
        closeModal(node);
        state.pendingSubmit = false;
        state.pendingNavigationUrl = '';
        state.pendingBackNavigation = false;
      });
    });
  };

  document.addEventListener('DOMContentLoaded', () => {
    qsa(document, '[data-product-poll-collection]').forEach(initSection);
  });
})();
